/**
 * @module app.um
 */

import * as UM from 'app/um/models/essette-api';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { validate } from 'app/shared/elements/ess-rule';
import { AuthChangeEvent } from 'app/um/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import { deepClone } from 'app/shared/lib/utilities';
import { IPopup } from 'app/shared/elements/popup-base';
import { DateTimeValue } from 'app/shared/lib/date-time';

@autoinject()
export class AuthStatusGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public auth: UM.Auth | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected headerLevelTat: boolean = true;

    protected authStatus: UM.AuthStatus = {};
    protected editHistory: UM.AuthStatusHistory | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 10;

    protected validate = validate;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.auth?.id) {
            return;
        }

        this.reload();

    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    public clear(): void {
        if(this.auth) {
            this.auth.history = {};
        }
    }

    public async reload(): Promise<void> {
        if(!this.auth) { return; }

        const [statuses] = await Promise.all([
            this.loadAuthStatusHistory()
        ]);

        this.auth.history = Object.assign(this.auth.history || {}, { statuses });
    }

    protected editStatusTimestamp(status: UM.AuthStatusHistory) {
        this.editHistory = deepClone(status);
        this.$modal.open();
    }

    protected async saveEffectiveStatusTime() {
        if(!this.editHistory) {
            return;
        }

        let saveHistory: UM.AuthStatusHistory = {
            id: this.editHistory.id,
            effectiveDateTime: this.editHistory.effectiveDateTime
        }

        if(await this.saveAuthHistory(saveHistory)) {
            this.$modal.close();
        }

    }

    protected isTatMet(status: UM.AuthStatusHistory): string {
        const endTimestamp = status?.metadata?.ended?.timestamp ? new DateTimeValue(status.metadata.ended.timestamp) : null;
        const dueDateTime = status.dueDateTime ? new DateTimeValue(status.dueDateTime) : null;
        const now = new DateTimeValue(this.env.now());
        const isOverdue = dueDateTime ? ((endTimestamp && endTimestamp.value) ? endTimestamp.isAfter(dueDateTime) : now.isAfter(dueDateTime)) : false;
        const isDetermination = status.status?.determination?.result !== "none";

        if(!dueDateTime) { return 'u'; }
        else if(isOverdue) { return 'n'; }
        else if(isDetermination) { return 'm'; }
        else if(!endTimestamp) { return 'u'; }
        else { return 'm' }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthStatusHistory(): Promise<UM.AuthStatusHistory[]> {
        if(!this.auth?.id) { return []; }

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'status',
            'priority',
            'metadata.started.byUser',
            'metadata.effectiveOverridden.byUser',
            'status.reason'
        ];

        const fields: string[] = [
            'dueDateTime',
            'effectiveDateTime',
            'metadata.effectiveOverridden.byUser.description',
            'metadata.effectiveOverridden.timestamp',
            'metadata.ended.timestamp',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
            'priority.description',
            'status.description',
            'status.determination.result',
            'status.flag.allowStatusDateEdit',
            'status.reason.description',
            'turnAroundTime.unit',
            'turnAroundTime.value'
        ];

        if(this.headerLevelTat) includes.push('turnAroundTime');

        try {
            this.state.beginLoad();

            const response = await this.authService.loadStatusHistoryByAuth(this.auth.id, options, includes, fields);
            var r = response.results;
            this.resultCount = response.count || 0;
            return r;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveAuthHistory(history: UM.AuthStatusHistory): Promise<boolean> {

        try {
            this.state.beginSave();
            const response = await this.authService.saveAuthHistory(history);
            this.toaster.standard.saved('um:auth.status.history.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.events.publish(new AuthChangeEvent());
                this.editHistory = null;
                this.reload();
            }
            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
